import type { Coordinate, OverlayTemplate } from 'klinecharts';

const SAMPLES = 20;

/** Catmull-Rom interpolation for a single segment (P1 → P2), using P0/P3 as tangent guides */
function catmullRomSegment(
  p0: Coordinate,
  p1: Coordinate,
  p2: Coordinate,
  p3: Coordinate,
  samples: number,
): Coordinate[] {
  const pts: Coordinate[] = [];
  for (let i = 0; i <= samples; i++) {
    const t  = i / samples;
    const t2 = t * t;
    const t3 = t2 * t;
    const x = 0.5 * (
      2 * p1.x
      + (-p0.x + p2.x) * t
      + (2 * p0.x - 5 * p1.x + 4 * p2.x - p3.x) * t2
      + (-p0.x + 3 * p1.x - 3 * p2.x + p3.x) * t3
    );
    const y = 0.5 * (
      2 * p1.y
      + (-p0.y + p2.y) * t
      + (2 * p0.y - 5 * p1.y + 4 * p2.y - p3.y) * t2
      + (-p0.y + 3 * p1.y - 3 * p2.y + p3.y) * t3
    );
    pts.push({ x, y });
  }
  return pts;
}

/**
 * Curve drawing tool — click to add points; the overlay draws a smooth
 * Catmull-Rom spline that passes through every clicked point.
 * Double-click (or pressing Escape) ends the drawing.
 */
const curve: OverlayTemplate = {
  name: 'curve',
  totalStep: Number.MAX_SAFE_INTEGER,
  needDefaultPointFigure: true,
  needDefaultXAxisFigure: true,
  needDefaultYAxisFigure: true,
  createPointFigures: ({ coordinates }) => {
    if (coordinates.length < 2) return [];

    const n = coordinates.length;
    const splineCoords: Coordinate[] = [];

    for (let i = 0; i < n - 1; i++) {
      const p0 = coordinates[Math.max(i - 1, 0)];
      const p1 = coordinates[i];
      const p2 = coordinates[i + 1];
      const p3 = coordinates[Math.min(i + 2, n - 1)];

      const seg = catmullRomSegment(p0, p1, p2, p3, SAMPLES);
      // Omit first point on all segments after the first to avoid duplicates
      splineCoords.push(...(i === 0 ? seg : seg.slice(1)));
    }

    return [{ type: 'line', attrs: { coordinates: splineCoords } }];
  },
};

export default curve;
